import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      // Check if user has companions, if not go to onboarding
      navigate('/dashboard');
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          t('auth.invalidCredentials', 'Invalid email or password'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      {/* Mobile container */}
      <div className="flex-1 flex flex-col max-w-mobile md:max-w-desktop mx-auto w-full">
        {/* Header */}
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col justify-center px-6 pb-8">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-primary-500 rounded-3xl mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl">✨</span>
            </div>
            <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text mb-2">
              {t('auth.welcome', 'Welcome back')}
            </h1>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
              {t('auth.signInToContinue', 'Sign in to continue to Chat Nexus')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2"
              >
                {t('auth.email', 'Email')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('auth.emailPlaceholder', 'you@example.com')}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2"
              >
                {t('auth.password', 'Password')}
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('auth.passwordPlaceholder', 'Enter your password')}
              />
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-500 hover:text-primary-400 transition-colors"
              >
                {t('auth.forgotPassword', 'Forgot password?')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
            >
              {isLoading ? t('auth.signingIn', 'Signing in...') : t('auth.signIn', 'Sign in')}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-dark-border dark:border-dark-border border-light-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-dark-bg dark:bg-dark-bg bg-light-bg text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                {t('auth.orContinueWith', 'or continue with')}
              </span>
            </div>
          </div>

          {/* Social Login */}
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => (window.location.href = '/api/auth/google')}
              className="p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-200 border border-dark-border dark:border-dark-border border-light-border rounded-xl transition-colors"
            >
              <svg
                className="w-5 h-5 mx-auto text-dark-text dark:text-dark-text text-light-text"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => (window.location.href = '/api/auth/apple')}
              className="p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-200 border border-dark-border dark:border-dark-border border-light-border rounded-xl transition-colors"
            >
              <svg
                className="w-5 h-5 mx-auto text-dark-text dark:text-dark-text text-light-text"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => navigate('/signup/phone')}
              className="p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-200 border border-dark-border dark:border-dark-border border-light-border rounded-xl transition-colors"
            >
              <svg
                className="w-5 h-5 mx-auto text-dark-text dark:text-dark-text text-light-text"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>

          {/* Sign up link */}
          <div className="text-center mt-6">
            <span className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
              {t('auth.dontHaveAccount', "Don't have an account?")}{' '}
            </span>
            <Link
              to="/signup"
              className="text-primary-500 hover:text-primary-400 font-medium transition-colors"
            >
              {t('auth.signUp', 'Sign up')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
